import type {
  Color,
  CommonColors,
  PaletteColor as MuiPaletteColor,
  PaletteOptions as MuiPaletteOptions,
  TypeText as MuiTypeText,
  PaletteColorOptions,
} from "@mui/material";

const common = {
  black: "#000",
  white: "#FFF",
} as const satisfies CommonColors;

const text = {
  primary: "rgba(0, 0, 0, 0.84)",
  secondary: "rgba(0, 0, 0, 0.60)",
  disabled: "rgba(0, 0, 0, 0.26)",
  hint: "rgba(0, 0, 0, 0.26)",
} as const satisfies MuiTypeText;

const grey = {
  50: "#FAFAFA",
  100: "#F5F5F5",
  200: "#EEEEEE",
  300: "#D4D4D4",
  400: "#BDBDBD",
  500: "#9E9E9E",
  600: "#757575",
  700: "#616161",
  800: "#424242",
  900: "#282828",
} as const satisfies Partial<Color>;

const emerald = {
  50: "#E8F5F0",
  100: "#C8E6DB",
  200: "#A5D6C3",
  300: "#80C8AC",
  400: "#4DB88F",
  500: "#43A881",
  600: "#419B78",
  700: "#388E6D",
  800: "#2C775A",
  900: "#1B5E44",
  A700: "#002919",
} as const satisfies Partial<Color>;

const deepPurple = {
  50: "#EEE9F6",
  100: "#D4C8E9",
  200: "#B8A4DB",
  300: "#9C7ECD",
  400: "#8762C1",
  500: "#7246B8",
  600: "#6941B2",
  700: "#5D38A8",
  800: "#5232A0",
  900: "#402692",
  A700: "#1D0E4E",
} as const satisfies Partial<Color>;

const blue = {
  50: "#E8F2FC",
  100: "#C0DFFF",
  200: "#99CCFF",
  300: "#73B8FF",
  400: "#5BA8FF",
  500: "#4C99FD",
  600: "#498AEE",
  700: "#4478DA",
  800: "#4066C8",
  900: "#3947A7",
  A700: "#222A63",
} as const satisfies Partial<Color>;

const pink = {
  50: "#F7E2ED",
  100: "#EDB7D3",
  200: "#E388B5",
  300: "#DB5998",
  400: "#D63381",
  500: "#C50D6A",
  600: "#B50C65",
  700: "#A3095F",
  800: "#8E0858",
  900: "#71024F",
  A700: "#4B0135",
} as const satisfies Partial<Color>;

const yellow = {
  50: "#FCF6E4",
  100: "#FCF0C7",
  200: "#FFE285",
  300: "#FFDB66",
  400: "#FFCE2E",
  500: "#FFC505",
  600: "#F7AE00",
  700: "#DF991A",
  800: "#C77700",
  900: "#9E5F00",
  A700: "#5C4000",
} as const satisfies Partial<Color>;

const purple = {
  50: "#F1E8F1",
  100: "#DDC4DD",
  200: "#C79FC7",
  300: "#B479B4",
  400: "#A85CA8",
  500: "#9B439D",
  600: "#8F3E96",
  700: "#7E378E",
  800: "#703185",
  900: "#562676",
  A700: "#2E014C",
} as const satisfies Partial<Color>;

const indigo = {
  50: "#EAECF3",
  100: "#C9CFE3",
  200: "#A6B1D1",
  300: "#8492BD",
  400: "#697AAF",
  500: "#4C62A8",
  600: "#465A9E",
  700: "#3C4F92",
  800: "#344687",
  900: "#263474",
  A700: "#010E4C",
} as const satisfies Partial<Color>;

/* const lightGreen = {
  50: "#EFF5E8",
  100: "#D7E6C8",
  200: "#BED6A5",
  300: "#A5C880",
  400: "#84B84D",
  500: "#77A843",
  600: "#6F9B41",
  700: "#648E38",
  800: "#53772C",
  900: "#3E5E1B",
  A700: "#1A3201",
} as const satisfies Partial<Color>;

const gold = {
  50: "#F6F5E9",
  100: "#EAE7C8",
  200: "#DEDAAB",
  300: "#C5BE6B",
  400: "#BAB14F",
  500: "#B0A745",
  600: "#A59D40",
  700: "#938B39",
  800: "#7D7631",
  900: "#585422",
  A700: "#34310E",
} as const satisfies Partial<Color>;
*/

const teal = {
  50: "#E9F6F6",
  100: "#D4EAED",
  200: "#89CDD7",
  300: "#61BBCC",
  400: "#48ADC5",
  500: "#34A0BF",
  600: "#2B93B3",
  700: "#1F81A1",
  800: "#1D708F",
  900: "#13516E",
  A700: "#00344C",
} as const satisfies Partial<Color>;

const orange = {
  50: "#FEF4E7",
  100: "#FDE3C3",
  200: "#FDD19E",
  300: "#FCBF7C",
  400: "#FAB168",
  500: "#F7A65F",
  600: "#F29045",
  700: "#E9813F",
  800: "#E0753E",
  900: "#CD6632",
  A700: "#70341E",
} as const satisfies Partial<Color>;

const brown = {
  50: "#efebe9",
  100: "#d7ccc8",
  200: "#bcaaa4",
  300: "#a1887f",
  400: "#8d6e63",
  500: "#795548",
  600: "#6d4c41",
  700: "#5d4037",
  800: "#4e342e",
  900: "#3e2723",
  A700: "#2B1B17",
} as const satisfies Partial<Color>;

const red = {
  50: "#ffebee",
  100: "#ffcdd2",
  200: "#ef9a9a",
  300: "#e57373",
  400: "#ef5350",
  500: "#f44336",
  600: "#e53935",
  700: "#d32f2f",
  800: "#c62828",
  900: "#b71c1c",
  A700: "#501C1B",
} as const satisfies Partial<Color>;

const green = {
  50: "#e8f5e9",
  100: "#c8e6c9",
  200: "#a5d6a7",
  300: "#81c784",
  400: "#66bb6a",
  500: "#4caf50",
  600: "#43a047",
  700: "#388e3c",
  800: "#2e7d32",
  900: "#1b5e20",
  A700: "#123F16",
} as const satisfies Partial<Color>;

const primary = {
  ...teal,
  light: teal[100],
  main: teal[800],
  dark: teal[900],
  contrastText: common.white,
} as const satisfies MuiPaletteColor;

const secondary = {
  ...teal,
  light: teal[50],
  main: teal[100],
  dark: teal[400],
  contrastText: teal.A700,
} as const satisfies MuiPaletteColor;

const dataViz = {
  dataViz01: {
    ...teal,
    light: teal[400],
    main: teal[600],
    dark: teal[700],
    contrastText: common.white,
    background: teal[50],
  },

  dataViz02: {
    ...orange,
    light: orange[300],
    main: orange[500],
    dark: orange[600],
    contrastText: common.white,
    background: orange[50],
  },

  dataViz03: {
    ...emerald,
    light: emerald[100],
    main: emerald[300],
    dark: emerald[500],
    contrastText: common.white,
    background: emerald[50],
  },

  dataViz04: {
    ...blue,
    light: blue[200],
    main: blue[400],
    dark: blue[600],
    contrastText: common.white,
    background: blue[50],
  },

  dataViz05: {
    ...pink,
    light: pink[100],
    main: pink[200],
    dark: pink[300],
    contrastText: common.white,
    background: pink[50],
  },

  dataViz06: {
    ...deepPurple,
    light: deepPurple[200],
    main: deepPurple[300],
    dark: deepPurple[500],
    contrastText: common.white,
    background: deepPurple[50],
  },

  dataViz07: {
    ...teal,
    light: teal[200],
    main: teal[300],
    dark: teal[500],
    contrastText: common.white,
    background: teal[50],
  },

  dataViz08: {
    ...red,
    light: red[200],
    main: red[300],
    dark: red[400],
    contrastText: common.white,
    background: red[50],
  },

  dataViz09: {
    ...yellow,
    light: yellow[100],
    main: yellow[200],
    dark: yellow[400],
    contrastText: common.white,
    background: yellow[50],
  },

  dataViz10: {
    ...brown,
    light: brown[100],
    main: brown[300],
    dark: brown[400],
    contrastText: common.white,
    background: brown[50],
  },

  dataViz11: {
    ...indigo,
    light: indigo[200],
    main: indigo[300],
    dark: indigo[400],
    contrastText: common.white,
    background: indigo[50],
  },

  dataViz12: {
    ...purple,
    light: purple[200],
    main: purple[300],
    dark: purple[400],
    contrastText: common.white,
    background: purple[50],
  },

  dataViz13: {
    ...grey,
    light: grey[200],
    main: grey[400],
    dark: grey[600],
    contrastText: common.white,
    background: grey[50],
  },
} as const satisfies Record<
  `dataViz${string}`,
  MuiPaletteColor | PaletteColorOptions
>;

type DataViz = typeof dataViz;

const customPalette = {
  emphasis: {
    ...orange,
    light: orange[100],
    main: orange[400],
    dark: orange[600],
    contrastText: orange.A700,
  },
  candidate: {
    ...pink,
    light: pink[50],
    main: pink[300],
    dark: pink[700],
    contrastText: common.white,
    background: pink[50],
  },

  card: {
    light: "rgba(20, 20, 20, 0.25)",
    main: "#C4C4C4",
    dark: "rgba(200, 200, 200, 0.25)",
    contrastText: "#444",
  },

  chartGoal: {
    main: "#C2D4D4",
  },

  chartValue: dataViz.dataViz06,

  data: {
    ...emerald,
    light: emerald[50],
    main: emerald[500],
    dark: emerald[700],
    contrastText: emerald.A700,
    background: emerald[50],
  },

  design: {
    ...blue,
    light: blue[50],
    main: blue[500],
    dark: blue[700],
    contrastText: common.white,
    background: blue[50],
  },

  ingredient: {
    ...grey,
    light: grey[200],
    main: grey[500],
    dark: grey[700],
    background: grey[200],
    contrastText: grey[900],
  },

  material: {
    ...emerald,
    light: emerald[100],
    main: emerald[400],
    dark: emerald[800],
    contrastText: emerald[50],
    background: emerald[100],
  },

  measurement: {
    ...emerald,
    light: emerald[50],
    main: emerald[100],
    dark: emerald[800],
    contrastText: emerald[900],
    background: emerald[100],
  },

  process: {
    ...emerald,
    light: emerald[200],
    main: emerald[700],
    dark: emerald[800],
    contrastText: emerald[50],
    background: emerald[50],
  },

  predictor: {
    ...deepPurple,
    light: deepPurple[50],
    main: deepPurple[300],
    dark: deepPurple[500],
    contrastText: common.white,
    background: deepPurple[50],
  },
} as const satisfies Record<string, MuiPaletteColor | PaletteColorOptions>;

type CustomColors = typeof customPalette;

interface CustomPalette extends DataViz, CustomColors {
  "search-space": typeof customPalette.design;
}

export type CustomPaletteKey = keyof CustomPalette;

declare module "@mui/material" {
  interface PaletteColor {
    background?: string;
  }
  interface SimplePaletteColorOptions {
    background?: string;
  }

  interface Palette extends CustomPalette {}
  interface PaletteOptions extends CustomPalette {}

  interface TypeText {
    primary: string;
    secondary: string;
    disabled: string;
    hint: string;
  }
}

export const palette = {
  /**
   * Standard Colors
   */
  primary,
  secondary,
  text,
  background: {
    paper: common.white,
    default: grey[100],
  },
  common,
  divider: grey[300],
  grey,

  action: {
    active: "rgba(0, 0, 0, 0.54)",
    hover: "rgba(0, 0, 0, 0.04)",
  },

  /**
   * Statuses
   */
  error: {
    ...red,
    light: red[50],
    main: red[700],
    dark: red[900],
    contrastText: red[50],
    background: red[50],
  },
  warning: {
    ...yellow,
    light: yellow[100],
    main: yellow[800],
    dark: yellow[900],
    contrastText: yellow[50],
    background: yellow[100],
  },
  info: {
    ...teal,
    light: teal[100],
    main: teal[800],
    dark: teal[900],
    contrastText: teal[50],
    background: teal[100],
  },
  success: {
    ...green,
    light: green[50],
    main: green[700],
    dark: green[900],
    contrastText: green[50],
    background: green[100],
  },

  /**
   * Custom colors
   */
  ...customPalette,
  "search-space": customPalette.design,

  /**
   * Data Visualization colors
   */
  ...dataViz,
} as const satisfies MuiPaletteOptions;

type CompletePalette = typeof palette;

export type ColorName = {
  [K in keyof CompletePalette]: CompletePalette[K] extends {
    light: string;
    main: string;
    dark: string;
    contrastText: string;
    background?: string;
  }
    ? K
    : never;
}[keyof CompletePalette];

export type ColorNameWithBackground = {
  [K in ColorName]: CompletePalette[K] extends {
    background: string;
  }
    ? K
    : never;
}[ColorName];

type ColorOverrides = Record<ColorName, true>;

declare module "@mui/material" {
  interface AlertPropsColorOverrides extends ColorOverrides {}
  interface ButtonGroupPropsColorOverrides extends ColorOverrides {}
  interface ButtonPropsColorOverrides extends ColorOverrides {}
  interface CheckboxPropsColorOverrides extends ColorOverrides {}
  interface ChipPropsColorOverrides extends ColorOverrides {}
  interface CircularProgressPropsColorOverrides extends ColorOverrides {}
  interface FormControlPropsColorOverrides extends ColorOverrides {}
  interface FormLabelPropsColorOverrides extends ColorOverrides {}
  interface IconButtonPropsColorOverrides extends ColorOverrides {}
  interface InputBasePropsColorOverrides extends ColorOverrides {}
  interface LinearProgressPropsColorOverrides extends ColorOverrides {}
  interface RadioPropsColorOverrides extends ColorOverrides {}
  interface SliderPropsColorOverrides extends ColorOverrides {}
  interface SvgIconPropsColorOverrides extends ColorOverrides {}
  interface SwitchPropsColorOverrides extends ColorOverrides {}
  interface TextFieldPropsColorOverrides extends ColorOverrides {}

  interface MenuItemOwnProps {
    color?: ColorName | "default";
  }
  interface TooltipProps {
    color?: ColorName | "default";
  }
}
